// Backend · Alerts: the drawer for a protected rule. Its shape is fixed; its
// monitoring mode, and a system metric's two thresholds, stay editable.

import { useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { useRequiredFeedback } from '@/components/required-field';
import { MutationErrorLine } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { SyncPill } from '@/components/sync-pill';
import { adminAlertById, adminAlertRules, adminAlerts, healthMetrics } from '@/api/resources';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { usePixels, useSystemHealth } from '@/api/hooks';
import type { AlertMetricCatalogEntry, AlertRule, RuleExplainer } from '@/api/types';
import {
  activationHelpForRule,
  activationModeOf,
  activationOptionsForRule,
  type ActivationModeId,
} from '@/lib/alert-activation';
import { summarizeRule, unitSuffix, type TriggerFields } from '@/lib/alert-labels';
import { numericFieldError } from '@/lib/alert-validation';
import { ActivationSelector } from './backend-alerts-activation';
import { Field, Row2, invalidate } from './backend-alerts-parts';

/** One save: activation first, then thresholds; either half may be skipped. */
export interface ProtectedRuleSave {
  saveActivation: boolean;
  saveThresholds: boolean;
  /** An activation-only rescue save while the thresholds are invalid: keep the drawer open. */
  keepOpen?: boolean;
  activationValue: ActivationModeId;
  warnValue: number;
  criticalValue: number;
}

export interface ProtectedRuleDrawerProps {
  rule: AlertRule;
  metricCatalog?: readonly AlertMetricCatalogEntry[];
  explainer?: RuleExplainer | null;
  onClose: () => void;
}

// ── Protected-rule drawer: fixed shape, editable safety controls ──────────────
export function ProtectedRuleDrawer({ rule, metricCatalog = [], explainer = null, onClose }: ProtectedRuleDrawerProps) {
  const pixelsQuery = usePixels();
  const fieldIdBase = useId();
  const fid = (name: string) => `${fieldIdBase}-${name}`;
  const pixels = pixelsQuery.data ?? [];
  // Live readings: the same cache the topbar chip polls — zero extra requests.
  const liveMetricsQuery = useSystemHealth(true);
  const liveMetric = rule.metric_key
    ? (liveMetricsQuery.data?.metrics ?? []).find((m) => m.key === rule.metric_key)
    : null;
  const t: TriggerFields = rule.trigger || {};
  const {
    messages: systemRequiredMessages,
    attempt: systemRequiredAttempt,
    report: reportSystemRequired,
    clear: clearSystemRequired,
  } = useRequiredFeedback();
  const initialWarn = String(t.warn ?? '');
  const initialCritical = String(t.critical ?? '');
  const initialActivationMode = activationModeOf(rule);
  const [warn, setWarn] = useState(initialWarn);
  const [critical, setCritical] = useState(initialCritical);
  const [activationMode, setActivationMode] = useState<ActivationModeId>(initialActivationMode);
  // These baselines advance after each successful narrow write. If activation
  // saves and thresholds fail, Retry sends only the still-unsaved thresholds.
  const [savedWarn, setSavedWarn] = useState(initialWarn);
  const [savedCritical, setSavedCritical] = useState(initialCritical);
  const [savedActivationMode, setSavedActivationMode] = useState<ActivationModeId>(initialActivationMode);
  const activationOptions = activationOptionsForRule(rule, metricCatalog, {
    currentMode: activationMode,
    pixels,
    pixelsLoaded: pixelsQuery.data !== undefined,
  });
  const hasEditableThresholds = t.type === 'system_metric';
  // Direction comes from the server explainer when present; the seeded-shape
  // heuristic (higher-is-better metrics have warn > critical) is the fallback.
  const higherIsBetter = explainer?.direction
    ? explainer.direction === 'higher_is_better'
    : Number(t.warn) > Number(t.critical);
  // Read once as a plain string: the chain below names every known arm, and a
  // kind a newer server adds still falls through to its raw tag.
  const windowKind: string | undefined = t.window?.kind;
  const win = t.window?.kind === 'rolling_hours' ? `rolling ${t.window.n}h`
    : t.window?.kind === 'latest_complete_day' ? 'latest complete day'
    : t.window?.kind === 'rolling_days' ? `rolling ${t.window.n ?? 7}d`
    : t.window?.kind === 'aggregate30d' ? '30-day aggregate' : (windowKind ?? '');

  const saveM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: async ({ saveActivation, saveThresholds, activationValue, warnValue, criticalValue }: ProtectedRuleSave) => {
      const completed: string[] = [];
      try {
        // Monitoring is the safety control: commit it first so an operator's
        // request to turn a noisy rule Off is not lost to a later threshold
        // failure. Any partial commit is stated explicitly below.
        if (saveActivation) {
          await api.patch(`/admin/alert-rules/${encodeURIComponent(rule.id)}/activation`, {
            body: { activation_mode: activationValue },
          });
          setSavedActivationMode(activationValue);
          completed.push('monitoring mode');
        }
        if (saveThresholds) {
          await api.patch(`/admin/alert-rules/${encodeURIComponent(rule.id)}/thresholds`, {
            body: { warn: warnValue, critical: criticalValue },
          });
          setSavedWarn(String(warnValue));
          setSavedCritical(String(criticalValue));
          completed.push('thresholds');
        }
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        if (completed.length) {
          invalidate(adminAlertRules.all, healthMetrics.all);
          // Off resolves this rule's open incidents server-side.
          if (completed.includes('monitoring mode') && activationValue === 'off') {
            invalidate(adminAlerts.all, adminAlertById.all);
          }
          throw new Error(`${completed.join(' and ')} saved, but the remaining changes failed: ${detail}`);
        }
        throw error;
      }
    },
    onSuccess: (_data, vars) => {
      invalidate(adminAlertRules.all, healthMetrics.all);
      if (vars?.saveActivation && vars?.activationValue === 'off') {
        invalidate(adminAlerts.all, adminAlertById.all);
      }
      // keepOpen: an activation-only rescue save while the threshold fields
      // are invalid — the operator still has edits to finish.
      if (!vars?.keepOpen) onClose();
    },
  });

  const w = Number(warn);
  const c = Number(critical);
  const finite = Number.isFinite(w) && Number.isFinite(c);
  // Keep warn on the "first bad" side of critical for the metric's direction.
  const ordered = higherIsBetter ? w >= c : w <= c;
  const warnNumericError = numericFieldError(warn, 'Warning threshold');
  const criticalNumericError = numericFieldError(critical, 'Critical threshold');
  const orderingMessage = !warnNumericError && !criticalNumericError && !ordered && finite
    ? (higherIsBetter
        ? 'Warning must be greater than or equal to critical for this metric.'
        : 'Warning must be less than or equal to critical for this metric.')
    : null;
  const warnError = warnNumericError || orderingMessage;
  const criticalError = criticalNumericError || orderingMessage;
  const thresholdsChanged = hasEditableThresholds
    && (warn !== savedWarn || critical !== savedCritical);
  const activationChanged = activationMode !== savedActivationMode;
  const systemThresholdIssues = hasEditableThresholds ? [
    warnError ? { key: 'system-metric-warn', message: warnError } : null,
    criticalError ? { key: 'system-metric-critical', message: criticalError } : null,
  ].filter(Boolean) : [];
  const attemptSaveThresholds = () => {
    if (saveM.isPending) return;
    if (!thresholdsChanged && !activationChanged) return;
    if (thresholdsChanged && !reportSystemRequired(systemThresholdIssues)) {
      // The threshold half is invalid. Monitoring is the safety control, so a
      // mode change still commits alone — nothing invalid is sent, the field
      // errors stay visible, and the drawer stays open to finish the numbers.
      if (activationChanged) {
        saveM.mutate({
          saveActivation: true,
          saveThresholds: false,
          keepOpen: true,
          activationValue: activationMode,
          warnValue: w,
          criticalValue: c,
        });
      }
      return;
    }
    saveM.mutate({
      saveActivation: activationChanged,
      saveThresholds: thresholdsChanged,
      activationValue: activationMode,
      warnValue: w,
      criticalValue: c,
    });
  };

  return (
    <>

      <ResponsiveOverlay label="backend alerts details" onClose={onClose} className="drawer" style={{ width: 480 }}>
        <div className="drawer-hd">
          <div style={{ flex: 1 }}>
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' }}>Protected rule · structure locked</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>🔒 {rule.name}</div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: '4px 10px' }}>×</button>
        </div>

        <div className="drawer-body">
          <div className="panel" style={{ padding: 12, marginBottom: 14, fontSize: 12, color: 'var(--ink-2)' }}>
            {hasEditableThresholds
              ? 'This protected rule can’t be deleted or reshaped. Its monitoring mode and warning/critical thresholds remain editable; the thresholds are the single source every screen and health pill uses.'
              : 'This protected safety rule can’t be deleted or reshaped. Its monitoring mode remains editable.'}
          </div>
          <div className="muted mono" style={{ fontSize: 11, marginBottom: explainer ? 4 : 12 }}>
            {hasEditableThresholds
              ? `${rule.metric_key} · ${win} · ${higherIsBetter ? '↑ higher is better' : '↓ lower is better'}`
              : summarizeRule(rule, metricCatalog)}
          </div>
          {explainer && (
            <div style={{ marginBottom: 12 }}>
              <div className="field-hint" style={{ marginBottom: 6 }}>{explainer.summary}</div>
              <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-3)', margin: '8px 0 4px' }}>How it’s calculated</div>
              <div className="field-hint" style={{ marginBottom: 6 }}>{explainer.calculation}</div>
              {explainer.example && explainer.example !== '—' && (
                <div className="field-hint" style={{ marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>Example: </span>{explainer.example}
                </div>
              )}
              {(liveMetric || explainer.deep_link) && (
                <>
                  <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-3)', margin: '10px 0 4px' }}>Right now</div>
                  {liveMetric && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <SyncPill band={liveMetric.stale ? 'unknown' : liveMetric.band} label={
                        liveMetric.value == null
                          ? 'no reading yet'
                          : `${Number(liveMetric.value).toLocaleString(undefined, { maximumFractionDigits: 1 })}${unitSuffix(liveMetric.unit)} — ${liveMetric.stale ? 'stale' : liveMetric.band}`
                      }/>
                    </div>
                  )}
                  {liveMetric && liveMetric.breaching?.length > 0 && (
                    <div className="field-hint" style={{ marginBottom: 4 }}>
                      {liveMetric.breaching.slice(0, 3).map((b) => (
                        <div key={b.scope_ref}>
                          {b.label}: {Number(b.value).toLocaleString(undefined, { maximumFractionDigits: 1 })}{unitSuffix(liveMetric.unit)} — {b.band}
                        </div>
                      ))}
                      {liveMetric.breaching.length > 3 && <div>…and {liveMetric.breaching.length - 3} more breaching</div>}
                    </div>
                  )}
                  {explainer.deep_link && (
                    <Link to={explainer.deep_link} className="field-hint" style={{ display: 'inline-block', textDecoration: 'underline' }} onClick={onClose}>
                      View source data →
                    </Link>
                  )}
                </>
              )}
            </div>
          )}
          <Field label="Monitoring">
            <div style={{ display: 'grid', justifyItems: 'start', gap: 6 }}>
              <ActivationSelector
                value={activationMode}
                options={activationOptions}
                label={`Monitoring for ${rule.name}`}
                onChange={setActivationMode}
              />
              <div className="field-hint" style={{ margin: 0 }}>
                {activationHelpForRule(rule, metricCatalog, {
                  currentMode: activationMode,
                  pixels,
                  pixelsLoaded: pixelsQuery.data !== undefined,
                })}
              </div>
            </div>
          </Field>
          {hasEditableThresholds && <Row2>
            <Field controlId={fid('warning-threshold-1')}
              label="Warning threshold"
              fieldKey="system-metric-warn"
              message={systemRequiredMessages['system-metric-warn']}
              attempt={systemRequiredAttempt}
            >
              <input id={fid('warning-threshold-1')} type="number" step="any" className="input" style={{ width: '100%' }} value={warn} onChange={(e) => {
                setWarn(e.target.value);
                clearSystemRequired('system-metric-warn');
                clearSystemRequired('system-metric-critical');
              }} />
            </Field>
            <Field controlId={fid('critical-threshold-2')}
              label="Critical threshold"
              fieldKey="system-metric-critical"
              message={systemRequiredMessages['system-metric-critical']}
              attempt={systemRequiredAttempt}
            >
              <input id={fid('critical-threshold-2')} type="number" step="any" className="input" style={{ width: '100%' }} value={critical} onChange={(e) => {
                setCritical(e.target.value);
                clearSystemRequired('system-metric-warn');
                clearSystemRequired('system-metric-critical');
              }} />
            </Field>
          </Row2>}
          {hasEditableThresholds && <div className="field-hint" style={{ marginTop: 6 }}>
            {higherIsBetter
              ? 'Higher is better — warning should be ≥ critical (a value at/below the threshold breaches).'
              : 'Lower is better — warning should be ≤ critical (a value at/above the threshold breaches).'}
          </div>}
          {hasEditableThresholds && orderingMessage && (
            <div className="field-hint" style={{ color: 'var(--bad)', marginTop: 6 }}>
              {higherIsBetter ? 'Warning must be ≥ critical for this metric.' : 'Warning must be ≤ critical for this metric.'}
            </div>
          )}
          {saveM.error && <div style={{ marginTop: 8 }}><MutationErrorLine error={saveM.error} /></div>}
        </div>

        <div className="drawer-foot">
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} disabled={saveM.isPending || (!thresholdsChanged && !activationChanged)}
            onClick={attemptSaveThresholds}>
            {Icons.check} {saveM.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
